import React, { useEffect, useState } from 'react';
import { useUser } from '@/contexts/UserContext';
import { useProject } from '@/components/ProjectSelector';
import { SectionLayout } from '@/components/section-layout';
import { useEntityList } from '@/hooks/useEntityList';
import { exportEntityCsv } from '@/lib/csv';
import { fetchEntity, fetchTeamMemberLookup } from '@/lib/entity-api';
import { getSession as getCachedSession } from '@/lib/auth-session';
import { GenericTable, type GenericTableColumn } from '@/styles/GenericTable';
import { NewItemButton, SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { GenericTag } from '@/styles/GenericTag';
import { format } from 'date-fns';
import { DollarSign, Copy, Trash2, Download, Upload } from 'lucide-react';
import type { Session } from '@supabase/supabase-js';
import { ProposalsForm } from '@/styles/ProposalsForm';
import { getApiUrl } from '@/lib/api-config';
import { fetchProposalAttachmentRows, deleteProposalAttachment } from './store';
import { sendProposalEmail } from './proposal-email-service';

interface Lead {
  id: string;
  name: string;
  email: string;
}

interface Proposal {
  id: string;
  proposal_id: string;
  company_id: string;
  client_id: string | null;
  project_id?: string | null;
  template_id: string | null;
  name: string;
  content: string | null;
  status: string;
  total_amount: number;
  notes: string | null;
  valid_until?: string | null;
  sent_at?: string | null;
  accepted_at?: string | null;
  rejected_at?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  attachments: { count: number }[];
  attachment_count: number;
  comment_count: number;
}

interface FileAttachment {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_url: string;
  created_by: string;
  created_at: string;
}

// Stable reference for "no attachments yet" -- `proposalAttachments[id] || []`
// would otherwise allocate a brand-new array on every render whenever a
// proposal has no recorded attachments, changing the `attachments` prop's
// identity every time and re-triggering ProposalsForm's own
// `[initialData?.id, attachments, setUploadedFiles]` effect for no reason.
const EMPTY_ATTACHMENTS: FileAttachment[] = [];

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return format(date, 'dd/MM/yyyy');
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

export function ProposalsPage() {
  const { companyId, user } = useUser();
  const { selectedProject } = useProject();
  // The list: ONE server-driven query (project + search + sort + page) -- the database searches, sorts and pages every proposal the user
  // may see, also by its lead's name; the counters come with each row (hooks/useEntityList.ts).
  const list = useEntityList<any, Proposal>({
    entityType: 'proposal',
    projectId: selectedProject,
    enabled: !!companyId,
    mode: 'pages',
    includeCounts: true,
    defaultSort: { columnKey: 'proposal_id', direction: 'asc', sortKey: 'proposal_id' },
    mapRow: (row) => row,
  });
  const proposals = list.items;
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isNewProposalOpen, setIsNewProposalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProposal, setEditingProposal] = useState<Proposal | null>(null);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [originalEditingProposal, setOriginalEditingProposal] = useState<Proposal | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [proposalAttachments, setProposalAttachments] = useState<Record<string, FileAttachment[]>>({});
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (companyId) {
      fetchLeads();
      getSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Map proposal statuses to GenericTag status types
  const mapProposalStatusToTag = (status: string): 'draft' | 'sent' | 'approved' | 'rejected' | 'feedback' | 'revision' => {
    switch (status.toLowerCase()) {
      case 'accepted':
        return 'approved'; // Green for accepted
      case 'sent':
        return 'sent'; // Blue for sent
      case 'declined':
        return 'rejected'; // Red for declined
      case 'feedback':
        return 'feedback'; // Purple for feedback
      case 'revision':
        return 'revision'; // Teal for revision
      case 'draft':
      default:
        return 'draft'; // Gray for draft
    }
  };

  // Format status text with proper capitalization
  const formatProposalStatusText = (status: string): string => {
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  // The leads / clients a proposal can be addressed to: the company's active people with role client (generic lookup, by the API)
  const fetchLeads = async () => {
    if (!companyId) return;
    try {
      const people = await fetchTeamMemberLookup(['client']);
      setLeads(
        people
          .map((person) => ({ id: person.id, name: `${person.first_name ?? ''} ${person.last_name ?? ''}`.trim(), email: person.email }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };

  const getSession = async () => {
    setSession(getCachedSession());
  };

  const handleCreateProposal = async (formData: any) => {
    try {
      console.log('Starting proposal creation...');
      if (!companyId || !user?.id) {
        setError('No company ID or user ID found');
        return null;
      }

      // a proposal can be saved without a client (a draft): the client is required to SEND it

      console.log('Creating proposal with data:', {
        ...formData,
        company_id: companyId,
        created_by: user.id
      });

      // Use generic API for proposal creation
      const response = await fetch(getApiUrl('api/entities/proposal'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          company_id: companyId,
          client_id: formData.client_id || null,
          project_id: formData.project_id || null,
          name: formData.name,
          content: formData.content || null,
          status: formData.status,
          total_amount: formData.total_amount,
          notes: formData.notes || null,
          created_by: user.id,
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create proposal');
      }

      const result = await response.json();
      const data = result.data;

      console.log('Proposal created successfully:', data);

      // If files were queued in the form, upload them now that we have an ID
      if (data?.id && Array.isArray(formData.attachmentsFiles) && formData.attachmentsFiles.length > 0) {
        try {
          const { uploadProposalAttachments } = await import('@/features/leads/proposals/store');
          const uploaded = await uploadProposalAttachments(data.id, formData.attachmentsFiles, companyId, user.id);
          console.log('Queued attachments uploaded after create', { count: uploaded.length });
        } catch (uploadAfterCreateErr) {
          console.error('Error uploading queued attachments after create:', uploadAfterCreateErr);
        }
      }
      list.reload();
      setIsNewProposalOpen(false);
      setError(null);
      
      // Return the created proposal so it can be used for sending
      return data;
    } catch (error: any) {
      console.error('Error creating proposal:', error);
      setError(error.message || 'Failed to create proposal');
      throw error;
    }
  };

  const formatProposalId = (proposalId: string): string => proposalId;

  const handleRowSelect = (proposalId: string) => {
    setSelectedRows(prev => {
      if (prev.includes(proposalId)) {
        return prev.filter(id => id !== proposalId);
      }
      return [...prev, proposalId];
    });
  };

  const handleRowClick = async (proposal: Proposal) => {
    setEditingProposal(proposal);
    setOriginalEditingProposal(proposal);
    setIsEditModalOpen(true);

    try {
      // The rows come with their permanent KrewUp address (nothing to sign): the list and the preview use it straight away.
      const attachments = await fetchProposalAttachmentRows(proposal.id);
      setProposalAttachments(prev => ({ ...prev, [proposal.id]: attachments }));
    } catch (error) {
      console.error('Error fetching proposal details:', error);
    }
  };

  const getLeadName = (leadId: string | null) => {
    if (!leadId) return 'No Lead';
    const lead = leads.find(l => l.id === leadId);
    return lead?.name || leadId;
  };

  const handleSaveChanges = async (formData: any) => {
    try {
      console.log('🔄 [Proposals] handleSaveChanges called', { editingProposalId: editingProposal?.id, formData });
      
      if (!editingProposal) {
        console.error('❌ [Proposals] No editingProposal found');
        setError('No proposal selected for editing');
        return;
      }

      if (!user?.id || !companyId) {
        console.error('❌ [Proposals] Missing user or company ID', { userId: user?.id, companyId });
        setError('User session expired. Please refresh the page.');
        return;
      }

      // Handle client_id - it might already be converted by the form, or might be 'no-lead'
      const leadId = formData.client_id === 'no-lead' || formData.client_id === '' ? null : formData.client_id;
      
      const requestBody = {
        user_id: user.id,
        company_id: companyId,
        client_id: leadId,
        project_id: formData.project_id || null,
        name: formData.name,
        content: formData.content || null,
        status: formData.status,
        total_amount: formData.total_amount,
        notes: formData.notes || null,
      };

      console.log('📤 [Proposals] Sending update request', {
        url: getApiUrl(`api/entities/proposal/${editingProposal.id}`),
        method: 'PUT',
        body: requestBody
      });

      // Use generic API for proposal update
      const response = await fetch(getApiUrl(`api/entities/proposal/${editingProposal.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      console.log('📥 [Proposals] Response status', { status: response.status, ok: response.ok });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('❌ [Proposals] API error response', errorData);
        throw new Error(errorData.error || `Failed to update proposal (${response.status})`);
      }

      const result = await response.json();
      console.log('✅ [Proposals] Update successful', result);
      
      // Handle different response formats
      const data = result.data || result;
      
      if (!data) {
        console.error('❌ [Proposals] No data in response', result);
        throw new Error('Invalid response from server');
      }

      // Refresh the entire proposals list from server to ensure we have latest data
      list.reload();
      
      // Refresh attachments
      if (editingProposal.id) {
        fetchProposalAttachments(editingProposal.id);
      }
      
      setIsEditModalOpen(false);
      setEditingProposal(null);
      setOriginalEditingProposal(null);
      setError(null); // Clear any previous errors
      
      console.log('✅ [Proposals] Edit completed successfully');
      
      // Return the updated proposal so it can be used for sending
      return data;
    } catch (error: any) {
      console.error('❌ [Proposals] Error updating proposal:', error);
      setError(error.message || 'Failed to update proposal');
      throw error;
    }
  };

  const handleCloseEditModal = () => {
    // Check if form has unsaved changes by comparing current editingProposal to original
    // Note: This is a simple check - the form itself handles the actual dirty state
    if (JSON.stringify(editingProposal) !== JSON.stringify(originalEditingProposal)) {
      setIsDiscardModalOpen(true);
    } else {
      setIsEditModalOpen(false);
      setEditingProposal(null);
      setOriginalEditingProposal(null);
    }
  };

  const handleDiscardChanges = () => {
    setIsDiscardModalOpen(false);
    setIsEditModalOpen(false);
    setEditingProposal(null);
    setOriginalEditingProposal(null);
  };

  const fetchProposalAttachments = async (proposalId: string) => {
    try {
      const attachments = await fetchProposalAttachmentRows(proposalId);

      setProposalAttachments(prev => ({
        ...prev,
        [proposalId]: attachments
      }));

      // Update the proposal's attachment count
      list.setItems((prev) => prev.map((proposal) =>
        proposal.id === proposalId ? { ...proposal, attachment_count: attachments.length } : proposal
      ));
    } catch (error) {
      console.error('Error fetching attachments:', error);
    }
  };

  const handleDeleteAttachment = async (proposalId: string, attachmentId: string) => {
    try {
      if (!user?.id || !companyId) {
        console.error('User or company ID not available');
        return;
      }

      if (!(await deleteProposalAttachment(attachmentId, user.id))) {
        throw new Error('Failed to delete attachment');
      }

      // Update local state
      setProposalAttachments(prev => ({
        ...prev,
        [proposalId]: prev[proposalId].filter(att => att.id !== attachmentId)
      }));

      // Update the proposal's attachment count
      list.setItems((prev) => prev.map((proposal) =>
        proposal.id === proposalId ? { ...proposal, attachment_count: proposal.attachment_count - 1 } : proposal
      ));
    } catch (error) {
      console.error('Error deleting attachment:', error);
    }
  };

  // Handle copy selected proposals
  const handleCopySelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one proposal to copy.');
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to copy proposals');
      return;
    }

    try {
      // Get selected proposals
      const selectedProposals = (await list.exportRows(selectedRows)) as unknown as Proposal[];

      // Copy each selected proposal — don't include proposal_id, the API
      // generates it automatically.
      for (const proposal of selectedProposals) {
        const response = await fetch(getApiUrl('api/entities/proposal'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user.id,
            company_id: companyId,
            client_id: proposal.client_id,
            name: proposal.name,
            content: proposal.content || null,
            status: 'draft', // Always set to draft for copies
            total_amount: proposal.total_amount,
            notes: proposal.notes || null,
            created_by: user.id, // Current user
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Error copying proposal:', errorData);
          continue;
        }
      }

      // Refresh proposals list
      list.reload();
      setSelectedRows([]);
      alert(`Successfully copied ${selectedProposals.length} proposal(s).`);
    } catch (error) {
      console.error('Error copying proposals:', error);
      alert('Error copying proposals. Please try again.');
    }
  };

  // Handle delete selected proposals
  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one proposal to delete.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} proposal(s)? This action cannot be undone.`)) {
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to delete proposals');
      return;
    }

    try {
      for (const proposalId of selectedRows) {
        const response = await fetch(getApiUrl(`api/entities/proposal/${proposalId}`), {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user.id,
            company_id: companyId
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Error deleting proposal:', errorData);
          continue;
        }
      }

      // Refresh proposals list
      list.reload();
      setSelectedRows([]);
      alert(`Successfully deleted ${selectedRows.length} proposal(s).`);
    } catch (error) {
      console.error('Error deleting proposals:', error);
      alert('Error deleting proposals. Please try again.');
    }
  };

  // Handle export to CSV - using actual database column names
  const handleSendProposal = async (proposal: Proposal) => {
    if (!user?.id || !companyId) {
      setError('User session expired. Please refresh the page.');
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      const result = await sendProposalEmail({
        proposalId: proposal.id,
        userId: user.id,
        companyId: companyId,
      });

      if (result.success) {
        // Refresh proposals to get updated status
        list.reload();
        alert(result.message || 'Proposal sent successfully!');
        // Close modals if open
        if (isEditModalOpen) {
          setIsEditModalOpen(false);
          setEditingProposal(null);
          setOriginalEditingProposal(null);
        }
        if (isNewProposalOpen) {
          setIsNewProposalOpen(false);
        }
      } else {
        setError(result.error || 'Failed to send proposal');
      }
    } catch (error: any) {
      console.error('Error sending proposal:', error);
      setError(error.message || 'Failed to send proposal');
    } finally {
      setIsSending(false);
    }
  };

  // Export to CSV: the proposals selected in the table, or -- with nothing selected -- EVERY proposal the current project + search
  // match, read by the backend (not only the page that is showing).
  const handleExportSelected = () => exportEntityCsv(list.exportRows, selectedRows, 'proposals', 'proposals');

  // Define table columns (placed after all handler functions to avoid hoisting issues)
  const columns: GenericTableColumn<Proposal>[] = [
    {
      key: 'proposal_id',
      header: 'Proposal ID',
      width: '6.5rem',
      sortable: true,
      sortKey: 'proposal_id',
      render: (row) => <span className="font-medium">{formatProposalId(row.proposal_id)}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      width: '8rem',
      sortable: true,
      sortKey: 'name',
      render: (row) => <span className="font-medium">{row.name}</span>,
    },
    {
      key: 'client_id',
      header: 'Lead',
      width: '8rem',
      sortable: true,
      sortKey: 'client',
      render: (row) => ((row as any).client ? `${(row as any).client.first_name ?? ''} ${(row as any).client.last_name ?? ''}`.trim() : getLeadName(row.client_id)),
    },
    {
      key: 'total_amount',
      header: 'Amount',
      width: '5rem',
      sortable: true,
      sortKey: 'total_amount',
      render: (row) => formatCurrency(row.total_amount),
    },
    {
      key: 'status',
      header: 'Status',
      width: '5rem',
      sortable: true,
      sortKey: 'status',
      render: (row) => (
        <GenericTag 
          type="status" 
          value={mapProposalStatusToTag(row.status)}
        >
          {formatProposalStatusText(row.status)}
        </GenericTag>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      width: '5rem',
      sortable: true,
      sortKey: 'created_at',
      render: (row) => formatDate(row.created_at),
    },
    {
      key: 'attachments',
      header: 'Attachments',
      sortable: true,
      sortKey: 'attachment_count',
      width: '5rem',
      align: 'center' as const,
      render: (row) => row.attachment_count || 0,
    },
    {
      key: 'comments',
      header: 'Comments',
      sortable: true,
      sortKey: 'comment_count',
      width: '5rem',
      align: 'center' as const,
      render: (row) => row.comment_count || 0,
    },
  ];

  const menuItems = [
    {
      label: 'Copy',
      icon: <Copy className="w-4 h-4" />,
      onClick: handleCopySelected,
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDeleteSelected,
      destructive: true,
    },
    {
      label: 'Import',
      icon: <Upload className="w-4 h-4" />,
      onClick: () => console.log('Import clicked'),
    },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExportSelected,
    },
  ];

  // Don't block page - let table show with skeleton rows

  return (
    <SectionLayout title="Proposals">
      <div className="space-y-6">
        {/* Action Buttons */}
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            <NewItemButton
              label="New"
              onClick={() => setIsNewProposalOpen(true)}
            />
            <SearchButton
              onSearch={list.setSearchInput}
              placeholder="Search"
            />
            <ThreeDotMenuButton items={menuItems} />
          </div>
        </div>

        {/* Generic Table */}
        <GenericTable
          columns={columns}
          {...list.tableProps}
          rowKey={(row) => row.id}
          scrollContainerMaxWidth="calc(100vw - 19.2rem)"
          minTableWidth={550}
          onRowClick={handleRowClick}
          selectable={true}
          selectedKeys={selectedRows}
          onSelectionChange={setSelectedRows}
          emptyState="No proposals found"
        />

        {isNewProposalOpen && (
            <ProposalsForm
              mode="create"
              leads={leads}
              onSubmit={handleCreateProposal}
              onCancel={() => {
                setIsNewProposalOpen(false);
                setError(null);
              }}
              session={session}
            error={error}
            onSend={async (proposalId: string, proposalData?: any) => {
              // Use the proposal data passed from the form, or find it in the list
              let proposal: Proposal | undefined = undefined;
              
              if (proposalData) {
                // Use the saved proposal data directly
                proposal = proposalData as Proposal;
              } else {
                // Fallback: try to find in current list
                proposal = proposals.find(p => p.id === proposalId);
                
                // Not on the page that is showing (e.g. just created): read that one proposal through the API
                if (!proposal) {
                  proposal = await fetchEntity<Proposal>('proposal', proposalId, { includeCounts: true }).catch(() => undefined);
                }
              }
              
              if (proposal) {
                await handleSendProposal(proposal);
                setIsNewProposalOpen(false);
              } else {
                setError('Proposal not found. Please try again.');
              }
            }}
          />
        )}

        {isDiscardModalOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Discard Changes?</h3>
            <div className="flex justify-end gap-4 mt-4">
              <Button type="button" variant="outline" onClick={() => setIsDiscardModalOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleDiscardChanges}
              >
                Discard Changes
              </Button>
            </div>
            </div>
              </div>
            )}

        {isEditModalOpen && editingProposal && (
              <ProposalsForm
                mode="edit"
                initialData={editingProposal}
                leads={leads}
                onSubmit={handleSaveChanges}
                onCancel={handleCloseEditModal}
                session={session}
                attachments={proposalAttachments[editingProposal.id] || EMPTY_ATTACHMENTS}
                onDeleteAttachment={(id) => handleDeleteAttachment(editingProposal.id, id)}
            error={error}
            onSend={async (proposalId: string, proposalData?: any) => {
              // Use the proposal data passed from the form, or find it in the list
              let proposal: Proposal | undefined = undefined;
              
              if (proposalData) {
                // Use the saved proposal data directly
                proposal = proposalData as Proposal;
              } else {
                // Fallback: try to find in current list
                proposal = proposals.find(p => p.id === proposalId);
                
                // Not on the page that is showing (e.g. just created): read that one proposal through the API
                if (!proposal) {
                  proposal = await fetchEntity<Proposal>('proposal', proposalId, { includeCounts: true }).catch(() => undefined);
                }
              }
              
              if (proposal) {
                await handleSendProposal(proposal);
              } else {
                setError('Proposal not found. Please try again.');
              }
            }}
              />
            )}
      </div>
    </SectionLayout>
  );
}

export default ProposalsPage;

